import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import i18n, { use } from 'i18next';
import { useTranslation } from "react-i18next";
import { ConfigProvider, Menu, Pagination, Tooltip } from 'antd';

import FormHeader from "../components/form/FormHeader";
import FormTitle from "../components/form/FormTitle";
import FormInput from "../components/form/FormInput";
import FormInputError from "../components/form/FormInputError";
import FormLabel from "../components/form/FormLabel";
import FormText from "../components/form/FormText";
import FormField from "../components/form/FormField";
import Flex from "../components/core/Flex";
import ShadowBox from "../components/core/ShadowBox";
import QuestionMark from "../components/form/QuestionMark";
import FormButtonDanger from "../components/form/FormButtonDanger";
import useHomeStore from "../store/useHomeStore";
import useViewStore from "../store/useViewStore";
import Footer from "../components/common/Footer";
import DropdownControl from "../components/core/DropdownControl";
import AdminPage from "../components/admin/AdminPage";
import HeaderBar from "../components/admin/HeaderBar";
import Space from "../components/core/Space";
import PageContentWrapper from "../components/core/PageContentWrapper";
import PageContentWrapper2 from "../components/core/PageContentWrapper2";
import NavigationBar from "../components/common/NavigationBar";
import Container from "../components/common/Container";
import Container2 from "../components/common/Container2";
import { apiDomain, basename } from "../setting";
import useAuthAdminStore from "../store/useAuthAdminStore";
import NavigationBarHome from "../components/common/NavigationBarHome";
import withErrorHandling from "../function/withErrorHandling";

function AdminSignIn() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { adminAuthenticate } = useAuthAdminStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const validateEmail = () => {
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // const isValid = emailRegex.test(email);
    const isValid = email !== "";
    setEmailError(!isValid);
    return isValid;
  };

  const validatePassword = () => {
    setPasswordError(password.trim() === "");
    return password.trim() !== "";
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  useEffect(() => {
    setEmailError(false);
  }, [email]);

  useEffect(() => {
    setPasswordError(false);
  }, [password]);

  return (
    <Page>
      <NavigationBarHome />

      <LeftBackground>
        <CopyRight>{t("translation:admin.signIn.copyRight", { key: new Date().getFullYear() })}</CopyRight>
      </LeftBackground>
      <RightBackground>
        <SignInBox>
          <form onSubmit={withErrorHandling(async (e) => {
            e.preventDefault();
            if (validateEmail() & validatePassword()) {
              const success = await adminAuthenticate(email, password);
              if (success) {
                navigate(`/${basename}/${i18n.language}/admin/application`);
              }
            }
          })}>
            <Logo src={`${process.env.PUBLIC_URL}/images/logo_blue-cross_bilingual.png`} alt="logo" />
            <FormHeaderH1>{t("translation:admin.signIn.preAuth")}</FormHeaderH1>
            <FormHeaderH2>{t("translation:admin.signIn.service_portal")}</FormHeaderH2>

            <FormField style={{ marginBottom: 16 }}>
              <FormLabel htmlFor="email">{t("translation:admin.signIn.email")}</FormLabel>
              <FormInput id="email" autocomplete="off" value={email} onChange={handleEmailChange} placeholder={t("pleaseInput", { key: t("translation:admin.signIn.email") })} aria-label={t("translation:admin.signIn.email")} />
              <FormInputError show={emailError}>* {t("pleaseInput", { key: t("translation:admin.signIn.email") })}</FormInputError>
            </FormField>

            <FormField>
              <FormLabel htmlFor="password">{t("translation:admin.signIn.password")}</FormLabel>
              <FormInput type={"password"} id="password" value={password} onChange={handlePasswordChange} placeholder={t("pleaseInput", { key: t("translation:admin.signIn.password") })} aria-label={t("translation:admin.signIn.password")} />
              <FormInputError show={passwordError}>* {t("pleaseInput", { key: t("translation:admin.signIn.password") })}</FormInputError>
            </FormField>

            <FormButtonDanger style={{ width: "100%" }}>{t("translation:admin.signIn.signIn")}</FormButtonDanger>
          </form>
        </SignInBox>

        <Terms>{t("translation:admin.signIn.disclaimer")} | {t("translation:admin.signIn.personal_data_collection")} | {t("translation:admin.signIn.privacy_policy")} | {t("translation:admin.signIn.cookie_policy")}</Terms>
      </RightBackground>
    </Page>
  );
}

const Page = styled(Flex)`

`;

const LeftBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background-image: url(${process.env.PUBLIC_URL}/images/admin_signin_1.png);
  background-size: cover;
  background-repeat: no-repeat;
`;

const CopyRight = styled(FormText)`
  position: absolute;
  bottom: 40px;
  left: 80px;
  font-size: 12px;
  line-height: 18px;
`;

const RightBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  background-image: url(${process.env.PUBLIC_URL}/images/admin_signin_2.png);
  background-size: cover;
  background-repeat: no-repeat;
`;

const SignInBox = styled(ShadowBox)`
  position: absolute;
  top: 50%;
  left: 40%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: auto;
  padding: 40px 56px;
  gap: 0;
`;

const FormHeaderH1 = styled.h1`
  font-size: 32;
  line-height: "44px";
  color: var(--aia-bx-dark-blue);
  margin-top: 24px;
  margin-bottom: 4px;
`;

const FormHeaderH2 = styled(FormHeader)`
  color: var(--aia-bx-blue-2);
`;

const Logo = styled.img`
  width: 226px;
  height: 48px;
`;

const Terms = styled(FormText)`
  position: absolute;
  bottom: 40px;
  right: 80px;
  font-size: 12px;
  line-height: 18px;
  color: #FFF;
`;

export default AdminSignIn;